import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ChartComponent, LoadingStateComponent } from '@sb/ui';
import { Subscription } from 'rxjs';

import { GraphMode, GraphSize } from '../../enums';
import { ChartPayload } from '../../models';
import { ChartApiService } from '../../services/chart-api.service';
import { ChartHelperService, ChartLineChart } from '../../services/chart-helper.service';

@Component({
  selector: 'app-worked-hours-graph',
  templateUrl: './worked-hours-graph.component.html',
  standalone: true,
  imports: [ChartComponent, NgClass, LoadingStateComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkedHoursGraphComponent implements OnDestroy {
  private readonly chartHelper = inject(ChartHelperService);
  private readonly chartApi = inject(ChartApiService);

  public readonly chartPayload = input.required<ChartPayload>();
  public readonly mode = input<GraphMode>('graph');
  public readonly size = input<GraphSize>('medium');

  public height = computed(() => this.chartHelper.getHeight(this.size()));

  public chart = signal<ChartLineChart>(undefined);
  public isLoading = signal(false);

  private loadSubscription: Subscription | undefined;

  public constructor() {
    effect(
      () => {
        this.loadData();
      },
      { allowSignalWrites: true },
    );
  }

  private loadData() {
    this.isLoading.set(true);
    const payload = this.chartPayload();

    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.chartApi.schedule(payload).subscribe((chartResponse) => {
      this.chart.set(
        this.chartHelper.convertResponseToLineChart(chartResponse, 'differences', 'difference_percentage'),
      );
      this.isLoading.set(false);
      this.loadSubscription = undefined;
    });
  }

  public ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }
}
